import { create } from 'zustand'
import { senderEmailsApi } from '@/lib/api'
import { toast } from 'sonner'
import type { SenderEmail, CreateSenderEmailData } from '@/types/senderEmail'

interface SenderEmailState {
  senderEmails: SenderEmail[]
  verifiedSenders: SenderEmail[]
  isLoading: boolean

  fetchSenderEmails: () => Promise<void>
  fetchVerifiedSenders: () => Promise<void>
  createSenderEmail: (data: CreateSenderEmailData) => Promise<SenderEmail>
  updateSenderEmail: (id: number, data: Partial<CreateSenderEmailData>) => Promise<void>
  deleteSenderEmail: (id: number) => Promise<void>
  verifySenderEmail: (id: number) => Promise<any>
  checkDns: (id: number) => Promise<any>
  setDefault: (id: number) => Promise<void>
  assignSender: (id: number, assignedUserIds: number[]) => Promise<void>

  // Reset — wipes per-user data on account switch (see main.tsx auth subscribe).
  reset: () => void
}

const initialState = {
  senderEmails: [] as SenderEmail[],
  verifiedSenders: [] as SenderEmail[],
  isLoading: false,
}

export const useSenderEmailStore = create<SenderEmailState>()((set, get) => ({
  ...initialState,

  fetchSenderEmails: async () => {
    set({ isLoading: true })
    try {
      const response = await senderEmailsApi.getAll()
      set({ senderEmails: response.data, isLoading: false })
    } catch {
      set({ isLoading: false })
      toast.error('Failed to fetch sender emails')
    }
  },

  fetchVerifiedSenders: async () => {
    try {
      const response = await senderEmailsApi.getVerified()
      set({ verifiedSenders: response.data })
    } catch {
      // silently fail
    }
  },

  createSenderEmail: async (data: CreateSenderEmailData) => {
    try {
      const response = await senderEmailsApi.create(data)
      const { senderEmails } = get()
      set({ senderEmails: [response.data, ...senderEmails] })
      toast.success('Sender email added successfully')
      return response.data
    } catch {
      toast.error('Failed to add sender email')
      throw new Error('Failed to add sender email')
    }
  },

  updateSenderEmail: async (id: number, data: Partial<CreateSenderEmailData>) => {
    try {
      const response = await senderEmailsApi.update(id, data)
      const { senderEmails } = get()
      set({ senderEmails: senderEmails.map(s => s.id === id ? response.data : s) })
      toast.success('Sender email updated')
    } catch {
      toast.error('Failed to update sender email')
      throw new Error('Failed to update sender email')
    }
  },

  deleteSenderEmail: async (id: number) => {
    try {
      await senderEmailsApi.delete(id)
      const { senderEmails } = get()
      set({ senderEmails: senderEmails.filter(s => s.id !== id) })
      toast.success('Sender email deleted')
    } catch {
      toast.error('Failed to delete sender email')
      throw new Error('Failed to delete sender email')
    }
  },

  verifySenderEmail: async (id: number) => {
    try {
      const response = await senderEmailsApi.verify(id)
      // Refresh the list to get updated status
      await get().fetchSenderEmails()
      if (response.data.status === 'verified') {
        toast.success('Email verified successfully')
      } else {
        toast.error('Verification failed. Check credentials.')
      }
      return response.data
    } catch {
      toast.error('Verification failed')
      throw new Error('Verification failed')
    }
  },

  checkDns: async (id: number) => {
    try {
      const response = await senderEmailsApi.checkDns(id)
      await get().fetchSenderEmails()
      toast.success('DNS check completed')
      return response.data
    } catch {
      toast.error('DNS check failed')
      throw new Error('DNS check failed')
    }
  },

  setDefault: async (id: number) => {
    try {
      await senderEmailsApi.setDefault(id)
      await get().fetchSenderEmails()
      toast.success('Default sender updated')
    } catch {
      toast.error('Failed to set default sender')
    }
  },

  assignSender: async (id: number, assignedUserIds: number[]) => {
    try {
      await senderEmailsApi.assign(id, assignedUserIds)
      await get().fetchSenderEmails()
      toast.success(assignedUserIds.length ? 'Mailbox access updated' : 'Mailbox unassigned. Only owners and admins can use it.')
    } catch {
      toast.error('Failed to update mailbox access')
    }
  },

  reset: () => set(initialState),
}))
